// @pages/workspace/Clients.page.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Either from "fp-ts/Either";
import * as Option from "@utils/Option";

import ClientsView from "@view/workspace/Clients";
import { useClientProjectClient } from "@contexts/ClientProjectClient.context";
import { useDocumentAssistantSupplyClient } from "@contexts/DocumentAssistantSupplyClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import { enabledFeatures, FeaturesEnum } from "@src/utils/enabledFeatures";
import type {
  Client,
  ClientProjectError,
  CreateClientRequest,
  CreateProjectRequest,
  PresignProjectDocumentsRequest,
  Project,
  ProjectDocument,
  UpdateProjectRequest,
} from "@ps-bridge/models/core/dataContracts";
import type { EffectTs, IOts, OptionTs, TaskTs } from "@ps-bridge/interop";
import { ok, type Ok } from "@ps-bridge/ok";
import { makeClientID } from "@ps-bridge/models/brands/clientID";
import { makeProjectID } from "@ps-bridge/models/brands/projectID";
import { unwrap as unwrapDocumentUploadFileName } from "@ps-bridge/models/brands/documentUploadFileName";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type {
  DownloadDocumentController,
  OpenDocumentController,
  UploadDocumentClient,
} from "@ps-bridge/contracts/clients";
import { renderRoute, routeClients, routeClientsExplorerWithClient, routeClientsExplorerWithProject } from "@navigation/typedRoutes";
import { ioEffect, taskEitherFromEffect, taskEitherTryCatch } from "@utils/effects/branded";
import { useRunTaskEitherWithError, useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { useOnInit, useOnRouteChange } from "@hooks/effects/useIoEffect";
import { useIoOptionState } from "@utils/useIoOptionState";
import { useIoState } from "@utils/useIoState";
import {
  peekDocumentUploadFile,
  removeDocumentUploadFile,
} from "@utils/documents/documentUploadRegistry";
import type {
  ClientProjectTaskClient,
  ClientsKernel,
  DownloadDocumentPayload,
  UploadDocumentPayload,
} from "@ps-bridge/contracts/clients";
import type { ClientsProgramInput } from "@ps-bridge/clients.program";
import { buildClientsViewProps } from "@ps-bridge/clients.program";
import type { SupplyError, SupplyId, SupplyStatusResponse } from "@services/clients/DocumentAssistantSupplyApi";
import {
  type SupplyAcceptedResponse,
  type SupplyApiDecodeResult,
  type SupplyManualUploadRequest,
  makeSupplyBatchId,
  makeSupplyByteDigest,
  makeSupplyDisplayName,
  makeSupplyId,
  makeSupplySourceHandle,
  makeSupplySourceId,
} from "@services/clients/DocumentAssistantSupplyApi";

const formatSupplyTaskError = (error: SupplyError) => ({
  message: error.message,
  info: Option.none<string>(),
});

const supplyAcceptedToStatus = (accepted: SupplyAcceptedResponse): SupplyStatusResponse => ({
  supplyId: accepted.supplyId,
  displayName: accepted.displayName,
  sourceId: Option.some(accepted.sourceId),
  classification: accepted.classification,
  indexing: accepted.indexing,
  retrieval: accepted.retrieval,
  uploadStatus: accepted.uploadStatus,
  listingId: accepted.listingId,
  provenance: accepted.provenance,
  trace: accepted.trace,
});

const bytesToHex = (bytes: ArrayBuffer): string =>
  Array.from(new Uint8Array(bytes))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

const digestFile = async (file: File): Promise<string> => {
  const digest = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return `sha256:${bytesToHex(digest)}`;
};

const createSupplyToken = (): string =>
  typeof window.crypto.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const firstDecodeError = (...results: SupplyApiDecodeResult<string>[]): string =>
  results.find((result) => result.type === "error")?.message ?? "Unable to build document supply request.";

const buildManualUploadRequest = async (file: File): Promise<SupplyApiDecodeResult<SupplyManualUploadRequest>> => {
  const supplyToken = createSupplyToken();
  try {
    const supplyId = makeSupplyId(`manual-${supplyToken}`);
    const batchId = makeSupplyBatchId(`manual-batch-${supplyToken}`);
    const displayName = makeSupplyDisplayName(file.name);
    const byteDigest = makeSupplyByteDigest(await digestFile(file));
    const sourceId = makeSupplySourceId(`manual-source-${supplyToken}`);
    const sourceHandle = makeSupplySourceHandle(`manual-upload:${supplyToken}`);
    if (
      supplyId.type === "ok" &&
      batchId.type === "ok" &&
      displayName.type === "ok" &&
      byteDigest.type === "ok" &&
      sourceId.type === "ok" &&
      sourceHandle.type === "ok"
    ) {
      return {
        type: "ok",
        value: {
          supplyId: supplyId.value,
          batchId: batchId.value,
          displayName: displayName.value,
          byteDigest: byteDigest.value,
          sourceId: sourceId.value,
          sourceHandle: sourceHandle.value,
          listingId: Option.none(),
        },
      };
    }
    return {
      type: "error",
      message: firstDecodeError(supplyId, batchId, displayName, byteDigest, sourceId, sourceHandle),
    };
  } catch (error) {
    return {
      type: "error",
      message: error instanceof Error ? error.message : "Unable to read selected supply file.",
    };
  }
};

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId: routeClientId, projectId: routeProjectId } = useParams();
  const clientProjectClient = useClientProjectClient();
  const supplyClient = useDocumentAssistantSupplyClient();
  const loading = useLoadingController();
  const runTaskWithError = useRunTaskWithError();
  const runSupplyTask = useRunTaskEitherWithError();

  const clientsState = useIoState<Client[]>([]);
  const projectsState = useIoState<Project[]>([]);
  const documentsState = useIoState<ProjectDocument[]>([]);
  const activeClientIdState = useIoOptionState<string>(Option.none());
  const activeProjectIdState = useIoOptionState<string>(Option.none());
  const newClientNameState = useIoState("");
  const newProjectTitleState = useIoState("");
  const isUploadingState = useIoState(false);
  const [supplyStatusRows, setSupplyStatusRows] = useState<SupplyStatusResponse[]>([]);
  const [supplySelectedFileName, setSupplySelectedFileName] = useState("");
  const [supplyError, setSupplyError] = useState("");
  const [isSupplySubmitting, setIsSupplySubmitting] = useState(false);

  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );

  const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);

  const effectToTaskEither = useCallback(
    <Value,>(effect: EffectTs<Value, ClientProjectError>) =>
      taskEitherFromEffect(effect, (reason) => reason as ClientProjectError),
    [],
  );

  const clientProjectTaskClient = useMemo<ClientProjectTaskClient>(
    () => ({
      listClients: effectToTaskEither(clientProjectClient.listClients),
      createClient: (request: CreateClientRequest) =>
        effectToTaskEither(clientProjectClient.createClient(request)),
      listProjects: (clientId: string) =>
        effectToTaskEither(clientProjectClient.listProjects(clientId)),
      createProject: (clientId: string) => (request: CreateProjectRequest) =>
        effectToTaskEither(clientProjectClient.createProject(clientId, request)),
      updateProject: (projectId: string) => (request: UpdateProjectRequest) =>
        effectToTaskEither(clientProjectClient.updateProject(projectId, request)),
      deleteProject: (projectId: string) =>
        effectToTaskEither(clientProjectClient.deleteProject(projectId)),
      listProjectDocuments: (projectId: string) =>
        effectToTaskEither(clientProjectClient.listProjectDocuments(projectId)),
      presignProjectDocuments: (clientId: string) => (projectId: string) => (
        request: PresignProjectDocumentsRequest,
      ) =>
        effectToTaskEither(
          clientProjectClient.presignProjectDocuments(clientId, projectId, request),
        ),
      deleteProjectDocument: (projectId: string) => (documentId: string) =>
        effectToTaskEither(clientProjectClient.deleteProjectDocument(projectId, documentId)),
    }),
    [clientProjectClient, effectToTaskEither],
  );

  const openDocument = useCallback(
    (url: string): IOts<Ok> =>
      ioEffect(() => {
        window.open(url, "_blank", "noopener");
        return ok;
      }),
    [],
  );

  const downloadDocument = useCallback(
    (payload: DownloadDocumentPayload): IOts<Ok> =>
      ioEffect(() => {
        const link = document.createElement("a");
        link.href = payload.url;
        link.download = payload.filename;
        link.rel = "noopener";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        link.remove();
        return ok;
      }),
    [],
  );

  const uploadDocument = useMemo<UploadDocumentClient>(
    () =>
      ((payload: UploadDocumentPayload) =>
        taskEitherTryCatch(
          () =>
            Option.fold(
              peekDocumentUploadFile(payload.file),
              () =>
                Promise.reject(
                  new Error(`Missing upload file for ${unwrapDocumentUploadFileName(payload.fileName)}`),
                ),
              (uploadFile) =>
                fetch(payload.uploadUrl, {
                  method: "PUT",
                  body: uploadFile,
                }).then((response) => {
                  if (!response.ok) {
                    return Promise.reject(
                      new Error(
                        `Upload failed for ${unwrapDocumentUploadFileName(payload.fileName)}`,
                      ),
                    );
                  }
                  removeDocumentUploadFile(payload.file);
                  return ok;
                }),
            ),
          (reason) => reason.message,
        )) as UploadDocumentClient,
    [],
  );

  const kernel: ClientsKernel = useMemo(
    () => ({
      loading,
      navigate: mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        }),
      ),
      clientsRoute: routeClients,
      clientsRouteWithClient: mkRouteBuilder((clientId) =>
        routeClientsExplorerWithClient(makeClientID(clientId)),
      ),
      clientsRouteWithProject: mkRouteBuilder(({ clientId, projectId }) =>
        routeClientsExplorerWithProject(makeClientID(clientId))(makeProjectID(projectId)),
      ),
      clientProjectClient: clientProjectTaskClient,
      uploadDocumentClient: uploadDocument,
      openDocumentController: openDocument as OpenDocumentController,
      downloadDocumentController: downloadDocument as DownloadDocumentController,
      clients: { read: clientsState.read, set: clientsState.set },
      projects: { read: projectsState.read, set: projectsState.set },
      documents: { read: documentsState.read, set: documentsState.set },
      activeClientId: {
        read: activeClientIdState.read,
        setSome: activeClientIdState.setSome,
        setMaybe: activeClientIdState.setMaybe,
      },
      activeProjectId: {
        read: activeProjectIdState.read,
        setSome: activeProjectIdState.setSome,
        setMaybe: activeProjectIdState.setMaybe,
      },
      newClientName: { read: newClientNameState.read, set: newClientNameState.set },
      newProjectTitle: { read: newProjectTitleState.read, set: newProjectTitleState.set },
      isUploading: { read: isUploadingState.read, set: isUploadingState.set },
      taskRunner,
    }),
    [
      activeClientIdState.read,
      activeClientIdState.setMaybe,
      activeClientIdState.setSome,
      activeProjectIdState.read,
      activeProjectIdState.setMaybe,
      activeProjectIdState.setSome,
      clientProjectTaskClient,
      clientsState.read,
      clientsState.set,
      documentsState.read,
      documentsState.set,
      downloadDocument,
      isUploadingState.read,
      isUploadingState.set,
      loading,
      navigate,
      newClientNameState.read,
      newClientNameState.set,
      newProjectTitleState.read,
      newProjectTitleState.set,
      openDocument,
      projectsState.read,
      projectsState.set,
      taskRunner,
      uploadDocument,
    ],
  );

  const programInput: ClientsProgramInput = useMemo(
    () => ({
      kernel,
      directUploadEnabled: enabledFeatures[FeaturesEnum.DirectS3Uploads],
    }),
    [kernel],
  );

  const viewProps = useMemo(() => buildClientsViewProps(programInput), [programInput]);

  const routeClientIdOption: OptionTs<string> = useMemo(
    () => (typeof routeClientId === "string" ? Option.some(routeClientId) : Option.none()),
    [routeClientId],
  );
  const routeProjectIdOption: OptionTs<string> = useMemo(
    () => (typeof routeProjectId === "string" ? Option.some(routeProjectId) : Option.none()),
    [routeProjectId],
  );

  const refreshSupplyStatus = useCallback(
    async (supplyId: SupplyId): Promise<Ok> => {
      const result = await runSupplyTask(supplyClient.status(supplyId), formatSupplyTaskError);
      if (Either.isLeft(result)) {
        setSupplyError(result.left.message);
        return ok;
      }
      setSupplyStatusRows((rows) => [
        result.right,
        ...rows.filter((row) => row.supplyId !== result.right.supplyId),
      ]);
      return ok;
    },
    [runSupplyTask, supplyClient],
  );

  const handleManualSupplyUpload = useCallback(
    async (fileList: OptionTs<FileList>): Promise<Ok> =>
      Option.fold(
        fileList,
        () => Promise.resolve(ok),
        async (files) => {
          if (files.length === 0) {
            return ok;
          }
          const file = files[0];
          setSupplySelectedFileName(file.name);
          setSupplyError("");
          setIsSupplySubmitting(true);
          const request = await buildManualUploadRequest(file);
          if (request.type === "error") {
            setSupplyError(request.message);
            setIsSupplySubmitting(false);
            return ok;
          }
          const accepted = await runSupplyTask(supplyClient.manualUpload(request.value), formatSupplyTaskError);
          if (Either.isLeft(accepted)) {
            setSupplyError(accepted.left.message);
            setIsSupplySubmitting(false);
            return ok;
          }
          setSupplyStatusRows((rows) => [
            supplyAcceptedToStatus(accepted.right),
            ...rows.filter((row) => row.supplyId !== accepted.right.supplyId),
          ]);
          const refreshed = await refreshSupplyStatus(accepted.right.supplyId);
          setIsSupplySubmitting(false);
          return refreshed;
        },
      ),
    [refreshSupplyStatus, runSupplyTask, supplyClient],
  );

  const handleRefreshSupplyStatus = useCallback(
    (supplyId: string): Promise<Ok> => {
      return refreshSupplyStatus(supplyId as SupplyId);
    },
    [refreshSupplyStatus],
  );

  useOnInit(viewProps.onInit);
  useOnRouteChange(routeClientIdOption, viewProps.onRouteClientChange);
  useOnRouteChange(routeProjectIdOption, viewProps.onRouteProjectChange);

  return (
    <ClientsView
      {...viewProps}
      clientCreationEnabled={enabledFeatures[FeaturesEnum.ClientCreation]}
      supplyStatusRows={supplyStatusRows}
      supplySelectedFileName={supplySelectedFileName}
      supplyErrorMessage={supplyError}
      isSupplySubmitting={isSupplySubmitting}
      onSelectSupplyFile={handleManualSupplyUpload}
      onRefreshSupplyStatus={handleRefreshSupplyStatus}
    />
  );
};

export default ClientsPage;
